<template>
  <v-container>
    <div class="headline">
      Dataset Creation Wizard
    </div>
    <v-container fluid class="pt-1 pb-1">
      <v-stepper v-model="step" vertical style="box-shadow:none;" non-linear>
        <v-stepper-step editable step="1" :complete="step > 1">
          Importing data
        </v-stepper-step>
        <v-stepper-content step="1">
          <v-row class="pb-3">
            <v-col class="text-center" cols="12">
              <h1 style="font-weight:300;">
                Start by choosing your importing method.
              </h1>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="xs" sm="12" md="12" lg="12">
              <v-row>
                <v-col class="xs pa" sm="12" md="4">
                  <div
                    class="upload_method"
                    :class="{
                      active: upload_method == 'empty'
                    }"
                    @click="handleUploadMethodSelection('empty')"
                  >
                    <v-icon>mdi-folder-open</v-icon>
                    <div class="title">
                      Empty dataset
                    </div>
                    <div class="details">
                      It's up to you to create your fields and append data.
                    </div>
                  </div>
                </v-col>
                <v-col class="xs pa" sm="12" md="4">
                  <div
                    class="upload_method"
                    :class="{
                      drag_enter: file_upload_method.drag_enter,
                      active: upload_method == 'file'
                    }"
                    @dragend="handDragLeaveFileUpload"
                    @dragleave="handDragLeaveFileUpload"
                    @dragenter="handDragEnterFileUpload"
                  >
                    <v-icon>mdi-content-save-outline</v-icon>
                    <div class="title">
                      Upload file
                    </div>
                    <div
                      v-if="
                        upload_method == 'file' &&
                          (isSchemaReady || !isWizardLoading)
                      "
                      class="details"
                    >
                      <v-icon class="fs-2 mr-2">
                        mdi-check-circle-outline </v-icon
                      >File uploaded successfully
                      <br />
                      <v-btn class="mt-2" color="success" small depressed>
                        Choose new file
                      </v-btn>
                    </div>
                    <div
                      v-else-if="file_upload_method.file !== null"
                      class="details"
                    >
                      <div>
                        <span v-if="isLoading.preview"
                          >Processing your file for preview</span
                        >
                        <span v-else>Getting your file</span>
                      </div>
                      <v-progress-linear
                        style="margin-top: 8px; width: 50%; display: inline-block;"
                        color="success"
                        indeterminate
                        rounded
                        height="6"
                      />
                    </div>
                    <div v-else class="details">
                      Drag your excel, csv or json file here to begin or click
                      to browse.
                    </div>
                    <input
                      ref="file_upload_input"
                      :disabled="
                        isLoading.preview ||
                          isLoading.upload ||
                          isLoading.schema
                      "
                      type="file"
                      @change="handleFileUploadChange($event)"
                    />
                  </div>
                </v-col>
                <v-col class="xs pa" sm="12" md="4">
                  <div
                    class="upload_method"
                    :class="{
                      active: upload_method == 'connector'
                    }"
                    @click="handleUploadMethodSelection('connector')"
                  >
                    <v-icon>mdi-cloud-download</v-icon>
                    <div class="title">
                      Connectors
                    </div>
                    <div class="details">
                      Connect to your data from Dropbox, Google Drive, FTP,
                      SFTP, etc.
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row
            v-if="upload_method == 'file' && (isSchemaReady || isPreviewing)"
            class="mt"
          >
            <v-col class="text-center">
              <div class="title mb-3">
                You file has been imported, go to the next step to preview your
                data.
              </div>
              <div class="mb-3">
                <v-btn @click="step = 2">
                  Next
                  <v-icon class="ml-2">
                    mdi-chevron-right
                  </v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
          <v-row
            v-else-if="upload_method == 'connector'"
            class="text-center pa"
          >
            <v-col class="xs mb">
              <h1 style="font-weight:300;">
                Choose your connector
              </h1>
            </v-col>
            <v-responsive width="100%" />
            <v-col class="xs offset-xs" cols="12">
              <v-text-field
                v-model="connections_method.searchInput"
                solo
                label="Search for your connector..."
                prepend-inner-icon="mdi-magnify"
                :clearable="true"
                @input="clearConnector"
              />
            </v-col>
            <v-col class="xs" sm="12" cols="12">
              <v-slide-x-transition
                v-if="filtered_integrations.length <= 0"
                leave-absolute
                class="wrap"
              >
                <v-col class="xs mt mb">
                  <h3>
                    Sorry, we could not find the connector you're looking for at
                    this very moment.
                  </h3>
                  <v-col cols="12" class="my-3">
                    <v-btn
                      class="mt-3"
                      :to="{ name: 'CreationIntegration' }"
                      target="_blank"
                      small
                      text
                    >
                      <v-icon left>
                        mdi-plus </v-icon
                      >create a new connector
                    </v-btn>
                    <br />
                    <v-btn class="mt-3" small text @click="getIntegrations">
                      <v-icon left>
                        mdi-reload </v-icon
                      >refresh
                    </v-btn>
                  </v-col>
                </v-col>
              </v-slide-x-transition>

              <v-slide-x-transition leave-absolute group>
                <v-row key="connectorsList" justify="center" align="center">
                  <template v-for="connector in filtered_integrations">
                    <v-col
                      :key="connector.id"
                      class="ma"
                      cols="12"
                      lg="2"
                      md="4"
                    >
                      <v-card
                        :key="connector.id"
                        class="ma-3 pa-6"
                        tile
                        height="100%"
                        :class="{
                          selected_connector:
                            connections_method.selectedIntegration === connector
                        }"
                        @click="handleSelectIntegrationConnector(connector)"
                      >
                        <v-img
                          class="mt-2"
                          :src="connector.integration.logo"
                          :height="50"
                          contain
                        />
                        <v-card-text class="pa-0 ma-0">
                          <div
                            class="mt-3 mb-1"
                            style="text-align:center; display:inline-block; width:100%;"
                          >
                            <h4>{{ connector.name }}</h4>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </template>
                </v-row>
              </v-slide-x-transition>
            </v-col>
            <v-col
              v-if="connections_method.selectedIntegration"
              sm="10"
              md="10"
              lg="6"
              offset-md="1"
              offset-lg="3"
              class="text-left xs offset-xs"
            >
              <v-form
                ref="form-connector"
                v-model="connections_method.formValid"
              >
                <v-row>
                  <v-col cols="12" class="text-center">
                    <h3>Connector's configuration</h3>
                  </v-col>
                  <v-col
                    v-for="(el, index) in connections_method.selectedIntegration
                      .integration.fields"
                    :key="index"
                    cols="12"
                  >
                    <v-text-field
                      v-model="
                        connections_method.selectedIntegration.values[el.name]
                      "
                      :type="el.to_hide ? 'password' : 'text'"
                      :append-icon="
                        typeof el.to_hide != 'undefined'
                          ? el.to_hide
                            ? 'mdi-eye'
                            : 'mdi-eye-off'
                          : ''
                      "
                      :disabled="el.readonly"
                      :placeholder="el.placeholder"
                      :label="el.label"
                      :rules="
                        el.required
                          ? [v => !!v || `${el.name} is required`]
                          : []
                      "
                      @keyup.enter="submit"
                      @click:append="
                        e => {
                          el.to_hide = !el.to_hide;
                        }
                      "
                    />
                  </v-col>
                  <v-col cols="6" class="text-center offset-3">
                    <v-btn
                      block
                      color="primary"
                      :disabled="isSaving"
                      :loading="isSaving"
                      @click="
                        upload_method == 'file'
                          ? prepareUploadedFile()
                          : handleGetConnectorFileButton()
                      "
                    >
                      <v-icon left>
                        mdi-cloud-download </v-icon
                      >Get file
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-col>
          </v-row>
          <v-row v-else-if="upload_method == 'empty'" />
        </v-stepper-content>
        <v-stepper-step editable step="2" :complete="step > 2">
          Data settings
        </v-stepper-step>
        <v-stepper-content step="2">
          <v-row>
            <v-col v-if="isLoading.preview" class="text-center">
              <v-progress-circular color="success" indeterminate :size="70" />
              <br />
              <h1 style="font-weight:300;" class="mt-2">
                We're processing your file for preview
              </h1>
              <small>
                Your file has been uploaded. Processing can take time depending
                on your file size.
                <br />
                <br />If processing is taking too much time, you may want to
                <br />
                <v-btn text small color="success" @click="cancelPreview"
                  >cancel and try again.</v-btn
                >
              </small>
            </v-col>
            <v-col v-else-if="isPreviewing">
              <v-row>
                <v-col class="text-center" cols="12">
                  <h1 style="font-weight:300;">
                    Select the worksheet you want to import
                  </h1>
                  <small>
                    This is a
                    <u>sample data</u> of each worksheet your file contains
                  </small>
                </v-col>
                <v-col cols="12" class="text-center">
                  <imported-file-options-btn
                    :options="dataset.hmdFile.options"
                    :type="dataset.hmdFile.type"
                    @update="
                      (key, val) => {
                        dataset.hmdFile.options[key] = val;
                      }
                    "
                    @submit="previewUploadedFile(dataset.hmdFile.id)"
                  />
                </v-col>
                <v-col cols="8" offset="2">
                  <v-tabs
                    v-model="dataset.hmdFile.options.sheet"
                    slider-color="primary"
                    color="grey lighten-4"
                  >
                    <v-tab
                      v-for="(s, i) in dataset.preview"
                      :key="i"
                      active-class="v-tabs__item--active black--text"
                    >
                      <v-icon class="mr-2">
                        mdi-google-spreadsheet
                      </v-icon>
                      {{ s.name }}
                    </v-tab>
                    <v-tab-item
                      v-for="(s, i) in dataset.preview"
                      :key="i"
                      :transition="false"
                      :reverse-transition="false"
                    >
                      <v-card text style="padding:0px;">
                        <v-data-table
                          class="preview elevation-1"
                          dense
                          fixed-header
                          disable-pagination
                          hide-default-footer
                          :headers="
                            _.map(s.schema.fields, (o, i) => {
                              return {
                                text: o.name,
                                value: o.name,
                                align: 'left',
                                sortable: false
                              };
                            })
                          "
                          :items="s.data"
                        >
                          <!-- <template
                            v-slot:items="props"
                          >
                            <td
                              v-for="(col,
                                      coli) in s.data[0]"
                              :key="coli"
                            >
                              {{
                                props.item[coli]
                              }}
                            </td>
                          </template>-->
                        </v-data-table>
                      </v-card>
                    </v-tab-item>
                  </v-tabs>
                </v-col>
              </v-row>
              <v-row>
                <!-- Start sync part -->
                <v-col
                  v-show="upload_method == 'connector' ? true : false"
                  cols="12"
                  class="text-center"
                >
                  <v-checkbox
                    v-model="dataset.resync.active"
                    color="primary"
                    label="Sync this dataset periodically"
                    :disabled="isSaving"
                  />
                  <v-col>
                    <v-row>
                      <v-col>
                        <v-select
                          v-model="dataset.resync.every"
                          prepend-icon="mdi-calendar-month"
                          :items="['Day', 'Week', 'Month']"
                          label="Sync this dataset every "
                          :disabled="isSaving || !dataset.resync.active"
                        />
                      </v-col>
                      <v-col v-if="dataset.resync.every != 'Day'">
                        <v-select
                          v-if="dataset.resync.every == 'Week'"
                          v-model="dataset.resync.dayOfWeek"
                          prepend-icon="mdi-view-week"
                          :items="weekDays"
                          label="Which day of week ?"
                          :disabled="isSaving || !dataset.resync.active"
                        />
                        <v-select
                          v-else-if="dataset.resync.every == 'Month'"
                          v-model="dataset.resync.dayOfMonth"
                          prepend-icon="mdi-calendar-today"
                          :items="monthDays"
                          label="Which day of month ?"
                          :disabled="isSaving || !dataset.resync.active"
                        />
                      </v-col>
                      <v-col>
                        <v-dialog
                          ref="dialog"
                          v-model="modal.time"
                          width="290px"
                          :return-value.sync="dataset.resync.at"
                        >
                          <template
                            v-slot:activator="{
                              on
                            }"
                          >
                            <v-text-field
                              v-model="dataset.resync.at"
                              :disabled="isSaving || !dataset.resync.active"
                              label="What time do you want to sync ?"
                              prepend-icon="mdi-update"
                              v-on="on"
                            />
                          </template>
                          <v-time-picker
                            v-if="dataset.resync.active"
                            v-model="dataset.resync.at"
                            style="height:450px;"
                            full-width
                          >
                            <v-spacer />
                            <v-btn
                              text
                              color="primary"
                              @click="modal.time = false"
                            >
                              Cancel
                            </v-btn>
                            <v-btn
                              text
                              color="primary"
                              @click="$refs.dialog.save(dataset.resync.at)"
                            >
                              OK
                            </v-btn>
                          </v-time-picker>
                        </v-dialog>
                      </v-col>
                    </v-row>
                    <v-radio-group
                      v-model="dataset.resync.RenewOrUpdate"
                      row
                      :disabled="isSaving || !dataset.resync.active"
                    >
                      <template v-slot:label>
                        <div class="mr-4">
                          Would you like to renew your data or just update it ?
                          <br />
                        </div>
                        <br />
                      </template>
                      <v-tooltip
                        top
                        style="margin-top:-2px; margin-left:-12px; padding-right:25px;"
                      >
                        <template
                          v-slot:activator="{
                            on
                          }"
                        >
                          <v-radio
                            label="Renew"
                            value="Renew"
                            color="primary"
                            v-on="on"
                            @click="dataset.resync.RenewOrUpdate = 'Renew'"
                          />
                        </template>
                        <span>
                          Truncate and insert imported data
                        </span>
                      </v-tooltip>

                      <v-tooltip
                        top
                        style="margin-top:-2px; margin-left:-12px;"
                      >
                        <template
                          v-slot:activator="{
                            on
                          }"
                        >
                          <v-radio
                            class="ml-5"
                            label="Update"
                            value="Update"
                            color="primary"
                            v-on="on"
                            @click="dataset.resync.RenewOrUpdate = 'Update'"
                          />
                        </template>
                        <span>
                          Insert new rows and update modified ones
                        </span>
                      </v-tooltip>
                    </v-radio-group>
                  </v-col>
                </v-col>
                <!-- End sync part -->
                <v-col class="text-center mt" cols="4" offset="4">
                  <v-btn
                    block
                    color="primary"
                    :loading="isSaving"
                    @click="handleSubmitWorksheet"
                  >
                    <v-icon left>
                      mdi-google-spreadsheet </v-icon
                    >Submit worksheet
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
            <v-col v-else-if="isSchemaReady">
              <v-row>
                <v-col cols="12">
                  <v-row>
                    <v-col class="text-center" cols="12">
                      <h1 style="font-weight:300;">
                        Setup your fields
                      </h1>
                    </v-col>
                    <v-col cols="12">
                      <v-alert
                        v-for="(w, i) in dataset.details"
                        :key="i"
                        :value="true"
                        type="warning"
                        text
                        icon="mdi-alert-box"
                        outlined
                      >
                        {{ w }}
                      </v-alert>
                    </v-col>
                    <v-col cols="12">
                      <v-row>
                        <v-col class="xs ml-3">
                          <v-switch
                            v-model="advancedEdition"
                            color="primary"
                            label="Advanced edition"
                          />
                        </v-col>
                        <v-col class="text-right xs" style="padding-top:3px;">
                          <v-btn
                            v-if="hasWorksheets"
                            depressed
                            text
                            class="mr-2"
                            @click="resetSelectedWorksheet"
                          >
                            <v-icon class="mr-2">
                              mdi-google-spreadsheet </v-icon
                            >Change worksheet
                          </v-btn>
                          <!-- METHOD resetChanges need to be defined -->
                          <!-- <v-btn
                            depressed
                            @click="resetChanges"
                          >
                            <v-icon class="mr-2">
                              mdi-backup-restore
                            </v-icon>Reset changes
                          </v-btn>-->
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="12">
                      <v-data-table
                        :headers="headers"
                        :items="dataset.fields"
                        hide-default-footer
                        disable-pagination
                      >
                        <template
                          v-slot:item="{
                            item,
                            index
                          }"
                        >
                          <tr
                            :class="{
                              'pending-delete': !item.exists
                            }"
                          >
                            <td v-if="advancedEdition" width="80px">
                              <span v-if="item.possible_pk">
                                <v-tooltip class="no-select" top>
                                  <template
                                    v-slot:activator="{
                                      on
                                    }"
                                  >
                                    <v-icon color="grey lighten-1" v-on="on"
                                      >mdi-key</v-icon
                                    >
                                  </template>
                                  <span>
                                    Eligible as primary key
                                  </span>
                                </v-tooltip>
                              </span>
                            </td>
                            <td>
                              <v-edit-dialog
                                :return-value.sync="item.updatedName"
                              >
                                {{ item.updatedName }}
                                <v-text-field
                                  slot="input"
                                  v-model="item.updatedName"
                                  label="Edit"
                                  single-line
                                  counter
                                  :disabled="isSaving"
                                  @focus="$event.target.select()"
                                />
                              </v-edit-dialog>
                            </td>
                            <td>
                              <v-select
                                v-model="item.type"
                                :items="dataTypes"
                                :disabled="
                                  (item.primary_key === true && !item.isNew) ||
                                    isSaving
                                "
                              />
                            </td>
                            <template v-if="advancedEdition">
                              <td>
                                <v-checkbox
                                  v-model="item.primary_key"
                                  color="primary"
                                  :disabled="isSaving"
                                  @change="
                                    e => {
                                      if (e) {
                                        dataset.fields[index].required = true;
                                        dataset.fields[index].editable = false;
                                      }
                                    }
                                  "
                                />
                              </td>
                              <td
                                v-if="
                                  dataset.fields.find(o => {
                                    return o.possible_required;
                                  })
                                "
                              >
                                <v-checkbox
                                  v-model="item.required"
                                  color="primary"
                                  :disabled="
                                    isSaving ||
                                      !item.possible_required ||
                                      item.primary_key
                                  "
                                  @change="
                                    e => {
                                      if (!e && item.primary_key)
                                        item.primary_key = false;
                                    }
                                  "
                                />
                              </td>
                              <td>
                                <v-checkbox
                                  v-model="item.editable"
                                  color="primary"
                                  :disabled="isSaving || item.primary_key"
                                />
                              </td>
                              <td class="text-center">
                                <v-btn
                                  text
                                  icon
                                  small
                                  :disabled="isSaving"
                                  @click.native="addColumn(item)"
                                >
                                  <v-icon>mdi-plus</v-icon>
                                </v-btn>
                                <v-btn
                                  text
                                  icon
                                  small
                                  :disabled="isSaving"
                                  @click.native="deleteColumn(item)"
                                >
                                  <v-icon v-if="item.exists">
                                    mdi-delete-outline
                                  </v-icon>
                                  <v-icon v-else>
                                    mdi-delete-forever
                                  </v-icon>
                                </v-btn>
                              </td>
                            </template>
                          </tr>
                        </template>
                      </v-data-table>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col class="xs offset-xs mt mb">
                  <v-btn block @click="dataset.fields = []">
                    <v-icon class="mr-2">
                      mdi-chevron-left </v-icon
                    >Previous
                  </v-btn>
                </v-col>
                <v-col class="xs mt pl-3 mb">
                  <v-btn block @click="step = 3">
                    Next
                    <v-icon class="ml-2">
                      mdi-chevron-right
                    </v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
            <v-col v-else-if="upload_method == 'empty'">
              <v-container class="fill-height">
                <v-row align="center">
                  <v-col class="text-center">
                    <div class="mb-2">
                      <v-icon class="display-3" color="grey">
                        mdi-folder-open
                      </v-icon>
                    </div>
                    <div class="title mb-5">
                      Creating an empty dataset is not concerned by this step.
                    </div>
                    <div class="mb-3">
                      <v-btn @click="step = 1">
                        <v-icon left>
                          mdi-chevron-left </v-icon
                        >Previous
                      </v-btn>
                      <v-btn class="ml-5" @click="step = 3">
                        Next
                        <v-icon right>
                          mdi-chevron-right
                        </v-icon>
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col v-else-if="upload_method == ''">
              <v-container class="fill-height">
                <v-row align="center">
                  <v-col class="text-center">
                    <div class="mb-3">
                      <v-icon class="fs-7 mr-2 pb-1">
                        mdi-folder-settings-variant </v-icon
                      >You have not finished setting up a data importing method.
                    </div>
                    <div class="mb-3">
                      <v-btn @click="step = 1">
                        <v-icon left>
                          mdi-chevron-left </v-icon
                        >Previous
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
          </v-row>
        </v-stepper-content>
        <v-stepper-step editable step="3" :complete="step > 3">
          Dataset informations
        </v-stepper-step>
        <v-stepper-content step="3">
          <v-form>
            <v-container>
              <v-row>
                <v-col md="12">
                  <v-text-field
                    v-model="dataset.informations.name"
                    required
                    label="Name"
                  />
                </v-col>
                <v-col class="xs">
                  <v-textarea
                    v-model="dataset.informations.description"
                    label="Description"
                  />
                </v-col>
              </v-row>
              <v-col class="xs">
                <input-labels v-model="dataset.informations.labels" ids />
              </v-col>
              <v-col class="xs" sm="6" md="3" lg="2">
                <v-switch
                  v-model="dataset.informations.isReadOnly"
                  color="primary"
                >
                  <div slot="label">
                    Read only
                    <v-tooltip class="no-select" top>
                      <template v-slot:activator="{ on }">
                        <v-icon small class="ml-1" v-on="on">
                          mdi-help-circle
                        </v-icon>
                      </template>
                      <span>
                        If checked, disable the dataset manipulation from the
                        website
                      </span>
                    </v-tooltip>
                  </div>
                </v-switch>
              </v-col>
              <v-col class="xs" sm="6" md="3" lg="2">
                <v-switch
                  v-model="dataset.informations.isPrivate"
                  color="primary"
                >
                  <div slot="label">
                    Private mode
                  </div>
                </v-switch>
              </v-col>
            </v-container>
          </v-form>
          <v-row>
            <v-dialog
              v-model="creationInProgressDialog"
              persistent
              max-width="400px"
            >
              <v-card>
                <v-card-title class="headline">
                  Dataset creation
                </v-card-title>
                <v-card-text>
                  Your dataset is being created, it may take a long time
                  depending on the file size. We will redirect you when the
                  creation is complete.
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn color="primary" text @click="handleClose">
                    Close
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-col cols="6" class="text-right">
              <v-btn block @click="step = 2">
                <v-icon left>
                  mdi-chevron-left </v-icon
                >Previous
              </v-btn>
            </v-col>
            <v-col cols="6" class="text-left">
              <v-btn
                :disabled="
                  isLoading.upload || isLoading.preview || isLoading.schema
                "
                block
                color="secondary"
                @click="submit"
              >
                <v-icon left>
                  mdi-table-plus </v-icon
                >Create dataset
              </v-btn>
            </v-col>
          </v-row>
        </v-stepper-content>
      </v-stepper>
    </v-container>
  </v-container>
</template>

<script>
// Services
import { CancelToken, isCancel } from "axios";
import IntegrationService from "./../Integrations/services/IntegrationService";
import DatasetService from "./services/DatasetService";
import ProjectService from "./../Projects/services/ProjectService";
import { slugify } from "../../../../utils/common";
import InputLabels from "../../../UIComponents/Inputs/Labels";

import ImportedFileOptionsBtn from "./ImportedFileOptionsBtn";

import { DATA_TYPES, WEEK_DAYS, MONTH_DAYS } from "./constants/main.js";
export default {
  components: {
    /* 'integration-card': IntegrationCard, */
    "input-labels": InputLabels,
    ImportedFileOptionsBtn
  },
  data() {
    return {
      request_activity: {
        preview: {
          request: null,
          cancelToken: null
        }
      },
      project: {
        id: this.$route.params.project || ""
      },
      modal: {
        time: false
      },
      advancedEdition: false,
      step: 1,
      upload_method: "",
      file_upload_method: {
        drag_enter: false,
        file: null
      },
      connections_method: {
        integrations: [],
        selectedIntegration: null,
        fileToGet: {
          dir: "",
          name: ""
        },
        nextPage: null,
        searchInput: "",
        formValid: true
      },
      dataset: {
        datasetFileUpload: {
          id: null
        },
        hmdFile: {
          id: null,
          options: {
            header_index: 0,
            offset: 0,
            limit: null,
            footer_index: null,
            delimiter: null,
            encoding: null,
            sheet: null
          }
        },
        preview: [],
        fields: [],
        details: [],
        informations: {
          items: ["tous", "private"],
          name: "",
          description: "",
          labels: [],
          isPrivate: true,
          isReadOnly: false
        },
        resync: {
          active: false,
          every: "Day",
          dayOfMonth: "1",
          dayOfWeek: "Monday",
          at: "12:00",
          RenewOrUpdate: "Renew"
        }
      },
      isSaving: false,
      dataTypes: DATA_TYPES,
      weekDays: WEEK_DAYS,
      monthDays: MONTH_DAYS,
      creationInProgressDialog: false,
      isLoading: {
        upload: false,
        preview: false,
        schema: false
      }
    };
  },
  computed: {
    headers: function() {
      const headers = [
        {
          text: "Column name",
          sortable: false,
          value: "name",
          align: "left"
        },
        {
          text: "Column type",
          sortable: false,
          value: "type",
          align: "left"
        }
      ];

      if (this.advancedEdition) {
        headers.unshift({
          test: "Possible PK",
          sortable: false,
          value: "possible_pk",
          align: "center"
        });
        headers.push({
          text: "Primary key",
          sortable: false,
          value: "primary_key",
          align: "left"
        });
        if (
          this.dataset.fields.find(o => {
            return o.possible_required;
          })
        ) {
          headers.push({
            text: "Required",
            sortable: false,
            value: "required",
            align: "left"
          });
        }
        headers.push({
          text: "Editable",
          sortable: false,
          value: "editable",
          align: "left"
        });
        headers.push({
          text: "Actions",
          width: "150px",
          sortable: false,
          value: "actions",
          align: "center"
        });
      }

      return headers;
    },
    hasWorksheets() {
      return this.dataset.preview.length > 0;
    },
    isWizardLoading() {
      return (
        this.isLoading.upload || this.isLoading.preview || this.isLoading.schema
      );
    },
    isPreviewing() {
      return (
        this.dataset.fields.length <= 0 &&
        this.dataset.preview.length > 0 &&
        this.dataset.hmdFile.id != null
      );
    },
    isSchemaReady() {
      return this.dataset.fields.length > 0;
    },
    filtered_integrations() {
      let temp = [];
      if (
        this.connections_method.searchInput
          ? this.connections_method.searchInput.replace(" ", "").length > 0
          : false
      ) {
        temp = this.connections_method.integrations.filter(integration => {
          return integration.name
            .toLowerCase()
            .includes(this.connections_method.searchInput.toLowerCase());
        });
      } else temp = this.connections_method.integrations;

      return _.orderBy(temp, "config_id", "asc");
    }
  },
  mounted() {
    this.getIntegrations();
    ProjectService.get(this, this.$route.params.project).then(r => {
      this.project = r.data;
    });
  },
  methods: {
    handleSelectIntegrationConnector(c) {
      const connector = _.cloneDeep(c);
      connector.integration.fields.push({
        label: "File directory",
        name: "directory",
        placeholder: "Enter file's directory",
        required: false
      });
      connector.integration.fields.push({
        label: "File name",
        name: "filename",
        placeholder: "Enter file name",
        required: true
      });
      connector.values.filename = "SampleXLSFile_904kb.xls";
      connector.values.directory = "";
      this.connections_method.selectedIntegration = connector;
    },
    clearConnector() {
      this.connections_method.selectedIntegration = null;
    },
    handleImportMethodChange() {
      this.clearDataset();
      this.clearFileInput();
      this.connections_method.selectedIntegration = null;
      this.connections_method.searchInput = "";
    },
    clearDataset() {
      this.dataset.fields = [];
      this.dataset.details = [];
      this.dataset.hmdFile.id = null;
    },
    clearFileInput() {
      this.$refs.file_upload_input.value = "";
      this.file_upload_method.file = null;
      this.dataset.hmdFile.id = null;
      this.file_upload_method.drag_enter = false;
      if (this.upload_method == "file") {
        this.upload_method = "";
      }
    },
    resetSelectedWorksheet() {
      this.dataset.fields = [];
    },
    resetChanges() {},
    addColumn(item) {
      var index = this.dataset.fields.findIndex(function(el) {
        return el === item;
      });
      this.dataset.fields.splice(index + 1, 0, {
        name: "column_name",
        primary_key: false,
        type: "text",
        exists: true,
        updatedName: "column_name",
        isNew: true,
        editable: true
      });
    },
    deleteColumn(item) {
      var index = this.dataset.fields.findIndex(function(el) {
        return el === item;
      });
      this.dataset.fields[index].exists = !this.dataset.fields[index].exists;
    },
    handleSubmitWorksheet() {
      return new Promise((resolve, reject) => {
        if (
          this.dataset.resync.RenewOrUpdate === "Renew" &&
          this.dataset.resync.active
        ) {
          this.$store.commit("confirm", {
            message:
              "Since sync 'renew' option will truncate then reimport all datas from file. This can cause a data loss. Are you sure you want that ?",
            title: "Warning",
            color: "warning",
            onCancel: () => {
              reject(new Error("cancel"));
            },
            onConfirm: () => {
              resolve("continue");
            }
          });
        } else {
          resolve("continue");
        }
      }).then(value => {
        if (["file", "connector"].includes(this.upload_method)) {
          this.schemaFromFile(this.dataset.hmdFile.id);
        }
      });
    },
    handleFileUploadChange(event) {
      this.file_upload_method.file = event.target.files[0];
      this.upload_method = "file";
      this.clearDataset();
      this.prepareUploadedFile();
    },
    handDragEnterFileUpload(e) {
      this.file_upload_method.drag_enter = true;
    },
    handDragLeaveFileUpload(e) {
      this.file_upload_method.drag_enter = false;
    },

    handleUploadMethodSelection(method) {
      this.upload_method = method;
      this.clearDataset();
      this.handleImportMethodChange();
      if (method === "empty") {
        this.step = 3;
      }
    },
    handleGetConnectorFileButton() {
      // Display a popup to inform user that each time the file will get sync
      // it will truncate et reimport data
      // and it is able to create a data loose
      // No need for a catch, if it is rejected we dont do anything

      if (this.$refs["form-connector"].validate()) {
        this.getConnectorFile();
      }
    },
    schemaFromFile(hmdFileId) {
      const self = this;
      self.isLoading.schema = true;
      DatasetService.schema(
        self,
        self.project.id,
        hmdFileId,
        self.dataset.hmdFile.options
      )
        .then(res => {
          self.dataset.fields = res.data.fields;
          self.dataset.datasetFileUpload.id = res.data.file_upload_id;
          self.dataset.details = res.data.detail;

          for (const item in self.dataset.fields) {
            self.dataset.fields[item].exists = true;
            self.dataset.fields[item].updatedName =
              self.dataset.fields[item].name;
          }
          self.dataset.fields = _.cloneDeep(self.dataset.fields);
          self.dataset.originalFields = _.cloneDeep(self.dataset.fields);
        })
        .finally(() => {
          self.isLoading.schema = false;
        });
    },
    prepareUploadedFile() {
      const file = this.file_upload_method.file;
      var fileName = file.name.toLowerCase();
      if (
        fileName.endsWith(".json") ||
        fileName.endsWith(".csv") ||
        fileName.endsWith(".xlsx") ||
        fileName.endsWith(".xls")
      ) {
        // Add filename to the Dataset name if not informed
        if (
          this.dataset.informations.name === undefined ||
          this.dataset.informations.name === null ||
          this.dataset.informations.name === ""
        ) {
          this.dataset.informations.name = fileName.substr(
            0,
            fileName.lastIndexOf(".")
          );
        }
        this.submitUploadedFile(file);
      } else {
        this.clearFileInput();
        this.$store.commit("push", {
          msg: "File must be of type JSON, CSV or XLS/XLSX",
          color: "error"
        });
      }
    },
    cancelPreview() {
      const req = this.request_activity.preview.request;
      const cancelToken = this.request_activity.preview.cancelToken;

      if (!_.isNil(req) && !_.isNil(cancelToken)) {
        cancelToken();
        this.request_activity.preview = {
          request: null,
          cancelToken: null
        };
      }
    },
    previewUploadedFile(hmdFileId) {
      const self = this;
      self.isLoading.preview = true;
      const headers = {
        cancelToken: new CancelToken(function(c) {
          self.request_activity.preview.cancelToken = c;
        })
      };
      self.request_activity.preview.request = DatasetService.preview(
        self,
        self.project.id,
        hmdFileId,
        self.dataset.hmdFile.options,
        headers
      )
        .then(res => {
          self.dataset.preview = res.data.preview;
          self.dataset.hmdFile.options = res.data.options;
          self.dataset.hmdFile.type = res.data.type;
          self.request_activity.preview = {
            request: null,
            cancelToken: null
          };
        })
        .catch(err => {
          if (isCancel(err)) {
            self.step = 1;
            self.clearFileInput();
            self.upload_method = "";
          }
        })
        .finally(() => {
          self.isLoading.preview = false;
        });
    },
    submitUploadedFile(file) {
      const t = this;
      t.isLoading.upload = true;
      DatasetService.upload(this, t.project.id, file)
        .then(response => {
          t.dataset.hmdFile.id = response.data.file_id;
          t.previewUploadedFile(t.dataset.hmdFile.id);
          t.step = 2;
        })
        .catch(err => {
          this.clearFileInput();
          this.$store.commit("push", {
            msg: "Error " + err,
            color: "error"
          });
        })
        .finally(() => {
          t.isLoading.upload = false;
        });
    },

    getConnectorFile() {
      const t = this;
      t.isLoading.upload = true;
      t.clearDataset();
      if (!this.connections_method.selectedIntegration.is_singerio) {
        DatasetService.uploadFromStorage(
          t,
          t.project.id,
          t.connections_method.selectedIntegration
        )
          .then(response => {
            t.dataset.hmdFile.id = response.data.file_id;
            t.previewUploadedFile(t.dataset.hmdFile.id);
            t.step = 2;
          })
          .finally(() => {
            t.isLoading.upload = false;
            // Add filename to the Dataset name if not informed
            if (
              this.dataset.informations.name === undefined ||
              this.dataset.informations.name === null ||
              this.dataset.informations.name === ""
            ) {
              const fileName = this.connections_method.selectedIntegration
                .values.filename;
              this.dataset.informations.name = fileName.substr(
                0,
                fileName.lastIndexOf(".")
              );
            }
          });
      }
    },
    getIntegrations() {
      const t = this;
      IntegrationService.list_configs(this).then(function(res) {
        t.connections_method.integrations = res.data.results;
      });
    },
    handleClose(e) {
      this.creationInProgressDialog = false;
      this.$emit("close");
    },
    submit() {
      const dataset = {
        name: this.dataset.informations.name,
        description: this.dataset.informations.description,
        labels: this.dataset.informations.labels,
        is_private: this.dataset.informations.isPrivate,
        is_readonly: this.dataset.informations.isReadOnly,
        is_global: false,
        project_id: this.project.id
      };
      if (this.upload_method === "file" && this.isPreviewing) {
        this.$store.commit("push", {
          msg: "Please submit a worksheet first and setup your field.",
          color: "warning"
        });
        this.step = 2;
        return;
      }
      if (this.upload_method === "empty") {
        DatasetService.create(dataset, this.project.id, this).then(r => {
          this.$emit("create", r.data);
          this.handleClose();
        });
        return;
      }
      dataset.file_upload_id = this.dataset.datasetFileUpload.id;
      dataset.fields = this.dataset.fields;
      if (this.upload_method === "connector") {
        const at = this.$moment(this.dataset.resync.at, "HH:mm");
        const schedule = {
          strategy: this.dataset.resync.RenewOrUpdate.toLowerCase(),
          frequency: this.dataset.resync.every.toLowerCase(),
          minutes: at.minutes(),
          hour: at.hours()
        };
        if (this.dataset.resync.every !== "Day") {
          schedule[
            this.dataset.resync.every === "Week" ? "day" : "date"
          ] = this.dataset.resync[
            this.dataset.resync.every === "Week" ? "dayOfWeek" : "dayOfMonth"
          ];
        }

        dataset.schedule = schedule;
      }
      this.creationInProgressDialog = true;
      DatasetService.create(dataset, this.project, this)
        .then(r => {
          this.$emit("create", r.data);
          this.handleClose();
        })
        .catch(() => {
          this.creationInProgressDialog = false;
        });
    }
  }
};
</script>

<style>
.upload_method.active.drag_enter {
  transition: 0.5s all;
  border: 2px solid #24c5b8 !important;
  background-color: #24c5b8 !important;
}

.upload_method.active {
  transition: 0.5s all;
  border: 2px solid #24c5b8;
}

.upload_method {
  height: auto;
  position: relative;
  transition: 0.5s all;
  cursor: pointer;
  border: 2px dashed lightgrey;
  border-radius: 5px;
  padding: 25px 30px;
  text-align: center;
}

.upload_method > .title {
  margin: 10px 0px;
}

.upload_method > i.v-icon {
  font-size: 60px;
  color: #bfbfbf;
}

.upload_method > input[type="file"] {
  opacity: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
}

.connector_card {
  cursor: pointer;
}
.preview td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}
.selected_connector {
  color: white !important;
  border: 2px solid #24c5b8 !important;
}

.fs-1 {
  font-size: 12px;
}

.fs-2 {
  font-size: 21px;
}

.fs-3 {
  font-size: 30px;
}

.fs-4 {
  font-size: 40px;
}

.fs-5 {
  font-size: 50px;
}

.fs-6 {
  font-size: 60px;
}

.fs-7 {
  font-size: 70px;
}

.fs-8 {
  font-size: 80px;
}
</style>
